// Ported from sn-plugin2 packages/embed-contract/src/{tabs,themeOptions,displayOptions,options}.ts, apps/playground/src/{setup/setup,settings/themeControls}.ts and packages/design-tokens/src/colors.ts @ 54685d64, presets @ f71f2180 — keep in step with the plugin.
import type {
  PluginAlign,
  PluginBrand,
  PluginThemePresetName,
  PluginCorners,
  PluginEmbedSettings,
  PluginOptionalColour,
  PluginPageColour,
  PluginPresetColour,
  PluginScheduleView,
  PluginView,
} from '../types/embedSettings'

export interface PluginViewTab {
  /** The route segment, and the `initialTab` value. */
  id: string
  /** The switch in the view's `tabs` section. */
  key: string
  label: string
}

/**
 * Every tab of each view's page, in page order. Labels are what the builder
 * shows; `id` and `key` are the plugin's own names and go into the snippet.
 */
export const PLUGIN_VIEW_TABS: Record<PluginView, PluginViewTab[]> = {
  organization: [
    { id: 'sub-orgs', key: 'subOrgs', label: 'Sub-orgs' },
    { id: 'competitions', key: 'competitions', label: 'Competitions' },
    { id: 'teams', key: 'teams', label: 'Teams' },
    { id: 'locations', key: 'locations', label: 'Locations' },
    { id: 'registrations', key: 'registrations', label: 'Registrations' },
  ],
  competition: [
    { id: 'playoffs', key: 'playoffs', label: 'Playoffs' },
    { id: 'schedule', key: 'schedule', label: 'Schedule' },
    { id: 'standings', key: 'standings', label: 'Standings' },
    { id: 'statistics', key: 'statistics', label: 'Statistics' },
    { id: 'suspensions', key: 'suspensions', label: 'Suspensions' },
    { id: 'announcements', key: 'announcements', label: 'Communication' },
  ],
  team: [
    { id: 'schedule', key: 'schedule', label: 'Schedule' },
    { id: 'statistics', key: 'statistics', label: 'Statistics' },
    { id: 'roster', key: 'roster', label: 'Roster' },
    { id: 'announcements', key: 'announcements', label: 'Communication' },
  ],
}

export const PLUGIN_VIEW_OPTIONS: { label: string; value: PluginView }[] = [
  { label: 'Organization', value: 'organization' },
  { label: 'Competition', value: 'competition' },
  { label: 'Team', value: 'team' },
]

/** The cfg key holding the entity id of each view. */
export const PLUGIN_VIEW_ID_FIELD: Record<
  PluginView,
  'orgId' | 'competitionId' | 'teamId'
> = {
  organization: 'orgId',
  competition: 'competitionId',
  team: 'teamId',
}

export const PLUGIN_VIEW_ID_LABEL: Record<PluginView, string> = {
  organization: 'Organization ID',
  competition: 'Competition ID',
  team: 'Team ID',
}

export const PLUGIN_FONT_SIZE_RANGE = { min: 14, max: 22 } as const
export const PLUGIN_SPACING_UNIT_RANGE = { min: 2, max: 8, step: 0.5 } as const
/** Letter spacing in px, for body text and headings. */
export const PLUGIN_LETTER_SPACING_RANGE = {
  min: -2,
  max: 5,
  step: 0.1,
} as const
export const PLUGIN_FOOTER_TEXT_MAX = 80
export const PLUGIN_FOOTER_LINK_MAX = 300
/** An encoded cfg longer than this is rejected by the plugin. */
export const PLUGIN_MAX_CFG_LENGTH = 4096
/** Where the plugin app runs locally; the preview's default when this app runs locally too. */
export const PLUGIN_PREVIEW_LOCAL_ORIGIN = 'http://localhost:5173'
/** The deployed plugin (every tenant; the key picks it); the preview's default everywhere else. */
export const PLUGIN_PREVIEW_PROD_ORIGIN = 'https://embed.sportninja.com'
/** Hostnames that count as a local machine, for this app and the plugin origin alike. */
export const PLUGIN_LOCAL_HOSTNAMES = ['localhost', '127.0.0.1']

export interface PluginColourSpec<N> {
  name: N
  /** Short label inside its group card. */
  label: string
  /** Full accessible name. */
  fullLabel: string
}

export interface PluginPageColourGroup {
  caption: string
  description: string
  colours: PluginColourSpec<PluginPageColour>[]
}

export interface PluginOptionalColourGroup {
  caption: string
  description: string
  /** What the plugin paints while the group's colours stay unset. */
  defaultHint: string
  colours: PluginColourSpec<PluginOptionalColour>[]
}

export const PLUGIN_PAGE_COLOUR_GROUPS: PluginPageColourGroup[] = [
  {
    caption: 'Page colours',
    description: 'Background, text, accent and borders of the whole plugin',
    colours: [
      { name: 'background', label: 'Background', fullLabel: 'Background' },
      { name: 'text', label: 'Text', fullLabel: 'Text' },
      { name: 'primary', label: 'Primary', fullLabel: 'Primary' },
      { name: 'border', label: 'Border', fullLabel: 'Border' },
    ],
  },
  {
    caption: 'Header colours',
    description: 'The band with the logo, title and tabs',
    colours: [
      {
        name: 'headerBackground',
        label: 'Background',
        fullLabel: 'Header background',
      },
      { name: 'headerText', label: 'Text', fullLabel: 'Header text' },
    ],
  },
]

export const PLUGIN_OPTIONAL_COLOUR_GROUPS: PluginOptionalColourGroup[] = [
  {
    caption: 'Content area colour',
    description: 'Behind the lists, tables and filters under the tabs',
    defaultHint: 'a shade of the background',
    colours: [
      {
        name: 'contentBackground',
        label: 'Background',
        fullLabel: 'Content background',
      },
    ],
  },
  {
    caption: 'Button colours',
    description: 'Primary, secondary and outline buttons',
    defaultHint: 'the brand default',
    colours: [
      {
        name: 'buttonBackground',
        label: 'Primary',
        fullLabel: 'Button background',
      },
      {
        name: 'secondaryButtonBackground',
        label: 'Secondary',
        fullLabel: 'Secondary button background',
      },
      {
        name: 'secondaryButtonBorder',
        label: 'Secondary border',
        fullLabel: 'Secondary button border',
      },
      {
        name: 'outlineButtonColor',
        label: 'Outline',
        fullLabel: 'Outline button colour',
      },
    ],
  },
  {
    caption: 'Link colour',
    description: 'Links in text, tables and breadcrumbs',
    defaultHint: 'the brand default',
    colours: [{ name: 'linkColor', label: 'Link', fullLabel: 'Link colour' }],
  },
  {
    caption: 'Input colours',
    description: 'Search boxes, selects and form fields',
    defaultHint: 'the brand default',
    colours: [
      {
        name: 'inputBackground',
        label: 'Background',
        fullLabel: 'Input background',
      },
      { name: 'inputBorder', label: 'Border', fullLabel: 'Input border' },
    ],
  },
  {
    caption: 'Schedule colours',
    description: 'Day headers, game rows and schedule cards',
    defaultHint: 'the brand default',
    colours: [
      {
        name: 'scheduleHeaderBackground',
        label: 'Day header',
        fullLabel: 'Schedule header background',
      },
      {
        name: 'scheduleBodyBackground',
        label: 'Rows and cards',
        fullLabel: 'Schedule body background',
      },
    ],
  },
  {
    caption: 'Playoffs bracket colour',
    description: 'Behind the playoffs bracket',
    defaultHint: 'the subtle surface',
    colours: [
      {
        name: 'bracketBackground',
        label: 'Background',
        fullLabel: 'Bracket background',
      },
    ],
  },
]

/** Which page colour an optional colour's swatch starts from. */
export const PLUGIN_COLOUR_START_FROM: Partial<
  Record<PluginOptionalColour, PluginPageColour>
> = {
  contentBackground: 'background',
  buttonBackground: 'primary',
  secondaryButtonBackground: 'background',
  secondaryButtonBorder: 'border',
  outlineButtonColor: 'primary',
  linkColor: 'primary',
  inputBackground: 'background',
  inputBorder: 'border',
  scheduleHeaderBackground: 'headerBackground',
  scheduleBodyBackground: 'background',
}

/** What the bracket swatch starts from when its subtle default is turned off. */
export const PLUGIN_BRACKET_START_COLOUR = '#0B2545'

/** Clearly different looks that set only the header band and the accent. */
export const PLUGIN_THEME_PRESETS: Record<
  PluginThemePresetName,
  Record<PluginPresetColour, string>
> = {
  ocean: {
    headerBackground: '#0B4F8A',
    headerText: '#FFFFFF',
    primary: '#1C7ED6',
  },
  forest: {
    headerBackground: '#1E5E3B',
    headerText: '#FFFFFF',
    primary: '#2F9E5B',
  },
  crimson: {
    headerBackground: '#8E1B2C',
    headerText: '#FFFFFF',
    primary: '#D6334A',
  },
  sunset: {
    headerBackground: '#F59F00',
    headerText: '#1A1A1A',
    primary: '#E8590C',
  },
  royal: {
    headerBackground: '#4C2A85',
    headerText: '#FFFFFF',
    primary: '#7950F2',
  },
}

/** What the plugin paints when a page colour is left unset. */
export const PLUGIN_BASE_COLOURS = {
  light: { bg: '#FFFFFF', ink: '#14171C', line: '#E1E5EB' },
  dark: { bg: '#0E1116', ink: '#EEF1F5', line: '#2A313B' },
} as const

export const PLUGIN_BRAND_ACCENT: Record<PluginBrand, string> = {
  sportninja: '#F2B400',
  canlan: '#004FFF',
}

/** In `auto` mode, a page background darker than this makes the plugin dark. */
export const PLUGIN_AUTO_DARK_BELOW_LUMINANCE = 0.2

export const PLUGIN_CORNERS_OPTIONS: { label: string; value: PluginCorners }[] =
  [
    { label: 'Rounded', value: 'rounded' },
    { label: 'Small', value: 'small' },
    { label: 'Square', value: 'square' },
  ]

/** Named spacing steps. */
export const PLUGIN_SPACING_OPTIONS: {
  label: string
  value: 'default' | 'compact'
}[] = [
  { label: 'Default', value: 'default' },
  { label: 'Compact', value: 'compact' },
]

export const PLUGIN_SCHEDULE_VIEW_OPTIONS: {
  label: string
  value: PluginScheduleView
}[] = [
  { label: 'Rows', value: 'rows' },
  { label: 'Cards', value: 'cards' },
  { label: 'Calendar', value: 'calendar' },
]

export const PLUGIN_ALIGN_OPTIONS: { label: string; value: PluginAlign }[] = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
]

export type PluginFooterMode = 'sportninja' | 'hidden' | 'custom'

export const PLUGIN_FOOTER_MODE_OPTIONS: {
  label: string
  value: PluginFooterMode
}[] = [
  { label: 'Branded', value: 'sportninja' },
  { label: 'Hidden', value: 'hidden' },
  { label: 'Custom', value: 'custom' },
]

export const PLUGIN_SCOREBOARD_OPTIONS: {
  label: string
  value: 'photo' | 'header'
}[] = [
  { label: 'Sport Photo', value: 'photo' },
  { label: 'Header Colours', value: 'header' },
]

/** The builder's starting point: every option at the plugin's default. */
export const DEFAULT_PLUGIN_EMBED_SETTINGS: PluginEmbedSettings = {
  view: 'organization',
  entityId: '',
  initialTab: '',
  syncUrl: true,
  hiddenTabs: { organization: [], competition: [], team: [] },
  mode: 'auto',
  colours: {},
  branding: {},
  display: {
    hideHeader: false,
    scheduleView: null,
    footer: null,
    todayGamesByDefault: false,
    headerNavigation: false,
    maxHeight: null,
    maxWidth: null,
    align: null,
    hideBorder: false,
    feedOrgId: null,
  },
}

/**
 * Display options the plugin reads only from the Script code: its no-script
 * `data-*` reader has no attribute for them. Labels match the builder's fields.
 */
export const PLUGIN_SCRIPT_ONLY_DISPLAY_OPTIONS: {
  key: 'todayGamesByDefault' | 'headerNavigation' | 'feedOrgId'
  label: string
}[] = [
  { key: 'todayGamesByDefault', label: "Today's games by default" },
  { key: 'headerNavigation', label: 'Header navigation' },
  { key: 'feedOrgId', label: 'Feed Org ID' },
]
